import { InvResourceClassEnum } from "@brewbase/db/types";
import { MISSING_VALUE } from "./attribute-formulas/type-registry";
const CHEMICAL_FIELDS = [
    { key: "formula", label: "Chemical Formula", type: "string" },
    { key: "cas_number", label: "CAS Number", type: "string" },
    {
        key: "molecular_weight_g_mol",
        label: "Molecular Weight (g/mol)",
        type: "number",
        formula: { name: "molecular_weight", unit: "g/mol" },
    },
    {
        key: "density_kg_m3",
        label: "Density (kg/m³)",
        type: "number",
        default: 1000,
        formula: { name: "density", unit: "kg/m3" },
    },
];
/**
 * Per-class metadata field definitions. Lifted from the inventory metadata
 * editor so the formula system and the editor share one definition.
 */
export const RESOURCE_CLASS_FIELDS = {
    [InvResourceClassEnum.INGREDIENT]: CHEMICAL_FIELDS,
    [InvResourceClassEnum.PROCESSING_AID]: CHEMICAL_FIELDS,
    // Food ingredients carry their attributes in the food_ingredient domain
    // tables, not inv_resource.metadata, so they expose no resource-class fields.
    [InvResourceClassEnum.FOOD_INGREDIENT]: [],
    [InvResourceClassEnum.AUXILIARY_MATERIAL]: [
        { key: "concentration", label: "Concentration", type: "string" },
        { key: "concentration_unit", label: "Concentration Unit", type: "string" },
        { key: "application_notes", label: "Application Notes", type: "string" },
        { key: "storage_temp_c", label: "Storage Temp (°C)", type: "number" },
        { key: "hazard_statements", label: "Hazard Statements", type: "string" },
        { key: "solvent", label: "Solvent", type: "string" },
    ],
    [InvResourceClassEnum.CONSUMABLE_SUPPLY]: [
        { key: "pack_size", label: "Pack Size", type: "string" },
        { key: "unit_per_pack", label: "Units per Pack", type: "number" },
        { key: "material", label: "Material", type: "string" },
        { key: "pore_size_um", label: "Pore Size (µm)", type: "number" },
        { key: "diameter_mm", label: "Diameter (mm)", type: "number" },
        { key: "volume_ml", label: "Volume (mL)", type: "number" },
        { key: "is_sterile", label: "Is Sterile", type: "boolean" },
        {
            key: "sterile_until_date",
            label: "Sterile Until (YYYY-MM-DD)",
            type: "string",
        },
    ],
    [InvResourceClassEnum.TRACKED_EQUIPMENT]: [
        { key: "serial_number", label: "Serial Number", type: "string" },
        { key: "material_type", label: "Material Type", type: "string" },
        { key: "max_uses", label: "Max Uses", type: "number" },
        {
            key: "max_sterilization_cycles",
            label: "Max Sterilization Cycles",
            type: "number",
        },
        {
            key: "calibration_due_date",
            label: "Calibration Due (YYYY-MM-DD)",
            type: "string",
        },
        {
            key: "autoclave_compatible",
            label: "Autoclave Compatible",
            type: "boolean",
        },
        { key: "cleaning_method", label: "Cleaning Method", type: "string" },
        { key: "pore_size_um", label: "Pore Size (µm)", type: "number" },
        { key: "volume_ml", label: "Volume (mL)", type: "number" },
        { key: "diameter_mm", label: "Diameter (mm)", type: "number" },
    ],
};
/** Fields of a class that are exposed as formula properties. */
export function formulaPropsForClass(cls) {
    return (RESOURCE_CLASS_FIELDS[cls] ?? []).filter((f) => f.formula);
}
/**
 * The full vocabulary of formula-exposable properties across every class,
 * deduped by member name. The runtime materialises this entire vocabulary onto
 * every inventory reference (valued where present, `missing` otherwise), so a
 * known property never hard-errors at eval time.
 */
export const FORMULA_PROPERTY_VOCAB = (() => {
    const byName = new Map();
    for (const fields of Object.values(RESOURCE_CLASS_FIELDS)) {
        for (const f of fields) {
            if (!f.formula || byName.has(f.formula.name))
                continue;
            byName.set(f.formula.name, {
                name: f.formula.name,
                unit: f.formula.unit,
                metadataKey: f.key,
                label: f.label,
            });
        }
    }
    return [...byName.values()];
})();
/** Quick-create metadata defaults for a class (fields carrying a `default`). */
export function defaultMetadataForClass(cls) {
    const out = {};
    for (const f of RESOURCE_CLASS_FIELDS[cls] ?? []) {
        if (f.default !== undefined)
            out[f.key] = f.default;
    }
    return out;
}
/**
 * Materialise an inventory reference's resource metadata into an object
 * EvalValue exposing the formula-property vocabulary. Each property is a scalar
 * (value + canonical unit) when the resource carries a strictly-positive finite
 * value, otherwise `missing` (covers null/undefined/0 — legacy seed rows store 0
 * as "unset", and every exposed property is a strictly-positive physical
 * quantity). Always returns the full object, even for null metadata, so
 * `ref.<property>` resolves to `missing` rather than an undefined-variable error.
 */
export function materializeInventoryRef(metadata) {
    const props = {};
    for (const def of FORMULA_PROPERTY_VOCAB) {
        const raw = metadata?.[def.metadataKey];
        const num = typeof raw === "number" ? raw : Number(raw);
        props[def.name] =
            raw == null || !isFinite(num) || num <= 0
                ? MISSING_VALUE
                : { type: "scalar", value: num, unit: def.unit };
    }
    return { type: "object", value: props };
}
//# sourceMappingURL=resource-properties.js.map